import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import { cn } from "../../lib/utils";

function NavTabIcon({ icon: Icon, className }) {
  const Resolved = Icon || LayoutDashboard;
  return <Resolved className={className} aria-hidden />;
}

export function tabActive(pathname, to) {
  if (to === "/") return pathname === "/";
  if (to === "/production") return pathname === "/production";
  if (to.endsWith("/dashboard")) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function navLinkExactEnd(to, siblingTos) {
  if (to === "/") return true;
  return siblingTos.some((other) => other !== to && other.startsWith(`${to}/`));
}

function NavBadge({ count }) {
  if (!count || count <= 0) return null;
  return (
    <span className="absolute -top-1 -right-2 min-w-[0.875rem] h-[0.875rem] px-0.5 rounded-full bg-red-500/90 text-white text-[8px] font-semibold flex items-center justify-center leading-none">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/**
 * Flat bottom navigation — only the active route is highlighted (sage + top indicator).
 */
export default function MobileCurvedBottomNav({ tabs = [] }) {
  const { pathname } = useLocation();
  const siblingTos = useMemo(() => tabs.map((n) => n.to), [tabs]);

  if (!tabs.length) return null;

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border/50 bg-card/96 backdrop-blur-md pb-[env(safe-area-inset-bottom)] shadow-[0_-1px_12px_rgba(0,0,0,0.04)]"
      data-testid="app-bottom-nav-curved"
      aria-label="Main navigation"
    >
      <div className="flex items-stretch justify-around max-w-lg mx-auto">
        {tabs.map((item) => {
          const active = tabActive(pathname, item.to);
          const label = item.shortLabel || item.label?.split(" ")[0] || "Tab";

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={navLinkExactEnd(item.to, siblingTos)}
              className="relative flex flex-1 flex-col items-center justify-center gap-1 min-h-[3.5rem] py-2 px-1 touch-manipulation"
              data-testid={`nav-tab-${item.iconKey || "item"}${item.mobilePrimary ? "-primary" : ""}`}
            >
              {active && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-7 h-[2px] rounded-full bg-bansal-sage"
                  aria-hidden
                />
              )}
              <span className="relative flex flex-col items-center gap-1">
                <span className="relative">
                  <NavTabIcon
                    icon={item.icon}
                    className={cn(
                      "h-[1.125rem] w-[1.125rem] transition-colors duration-150",
                      active ? "text-primary stroke-[2px]" : "text-muted-foreground/65 stroke-[1.75px]",
                    )}
                  />
                  <NavBadge count={item.badge} />
                </span>
                <span
                  className={cn(
                    "text-[10px] leading-none truncate max-w-[4.25rem] text-center transition-colors duration-150",
                    active ? "font-semibold text-primary" : "font-medium text-muted-foreground/75",
                  )}
                >
                  {label}
                </span>
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
